import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Field } from "@base-ui/react/field";
import { Input } from "@base-ui/react/input";
import css from "./TextInput.module.css";

const TextInput = React.forwardRef(
  (
    {
      bare = false,
      variant = "outlined",
      type = "text",
      label,
      description,
      error,
      leading,
      trailing,
      disabled = false,
      required = false,
      name,
      placeholder,
      value,
      defaultValue,
      onValueChange,
      validate,
      validationMode,
      validationDebounceTime,
      className,
      ...rest
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    if (bare) {
      return (
        <div className={clsx(css.inputWrapper, css[variant])}>
          {leading && <span className={css.adornment}>{leading}</span>}
          <Input
            ref={ref}
            className={css.input}
            type={type}
            name={name}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            onValueChange={onValueChange}
            required={required}
            disabled={disabled}
            {...rest}
          />
          {trailing && <span className={css.adornment}>{trailing}</span>}
        </div>
      );
    }

    return (
      <Field.Root
        className={clsx(css.root, className)}
        disabled={disabled}
        invalid={hasError}
        validate={validate}
        validationMode={validationMode}
        validationDebounceTime={validationDebounceTime}
      >
        {label && <Field.Label className={css.label}>{label}</Field.Label>}
        <div className={clsx(css.inputWrapper, css[variant])}>
          {leading && <span className={css.adornment}>{leading}</span>}
          <Input
            ref={ref}
            className={css.input}
            type={type}
            name={name}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            onValueChange={onValueChange}
            required={required}
            {...rest}
          />
          {trailing && <span className={css.adornment}>{trailing}</span>}
        </div>
        {description && !hasError && (
          <Field.Description className={css.description}>
            {description}
          </Field.Description>
        )}
        {typeof error === "string" && (
          <Field.Error className={css.error} match>
            {error}
          </Field.Error>
        )}
      </Field.Root>
    );
  },
);

TextInput.displayName = "TextInput";

TextInput.propTypes = {
  bare: PropTypes.bool,
  variant: PropTypes.oneOf(["outlined", "filled"]),
  type: PropTypes.oneOf(["text", "email", "tel", "url", "password"]),
  label: PropTypes.string,
  description: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  leading: PropTypes.node,
  trailing: PropTypes.node,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onValueChange: PropTypes.func,
  validate: PropTypes.func,
  validationMode: PropTypes.string,
  validationDebounceTime: PropTypes.number,
  className: PropTypes.string,
};

export default TextInput;
